import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    searchUrl: String,
    redirectUrl: { type: String, default: '/resource_categories' },
    minLength: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 600 }
  }

  static targets = ['input', 'results']

  connect() {
    this.input = this.element.querySelector('.resource-search-input');
    this.searchTimeout = null;
    this.isSearchPage = window.location.pathname.includes('/search');
    
    // Bind Turbo event handlers so they can be removed later
    this.boundHandleTurboLoad = this.handleTurboLoad.bind(this);
    this.boundHandleTurboRender = this.handleTurboRender.bind(this);
    
    if (this.input) {
      // Listen for input events
      this.input.addEventListener('input', this.handleInput.bind(this));
      this.input.addEventListener('keydown', this.handleKeydown.bind(this));
      
      // Handle search cancel button (X) click - prevent default and just clear
      this.input.addEventListener('search', (e) => {
        // This fires when the cancel button is clicked
        if (this.input.value === '') {
          e.preventDefault();
          // Clear the input and perform empty search without redirect
          this.input.value = '';
          if (this.isSearchPage) {
            const searchUrl = this.searchUrlValue || '/resources/search';
            window.history.pushState({}, '', searchUrl);
            this.performSearch('');
          }
        }
      });
      
      // Position cursor at end of text on initial load (autofocus handles focus)
      // Small delay to ensure autofocus has completed
      setTimeout(() => {
        this.positionCursorAtEnd();
      }, 50);
    }

    // Prevent form submission on Enter (we handle it via AJAX)
    this.element.addEventListener('submit', this.handleSubmit.bind(this));

    // Listen for Turbo events to position cursor after navigation/updates
    if (typeof Turbo !== 'undefined') {
      document.addEventListener('turbo:load', this.boundHandleTurboLoad);
      document.addEventListener('turbo:render', this.boundHandleTurboRender);
    }
  }

  handleTurboLoad() {
    // Update isSearchPage check after Turbo navigation
    this.isSearchPage = window.location.pathname.includes('/search');
    // Re-find input element in case DOM was replaced
    this.input = this.element.querySelector('.resource-search-input');
    if (this.input) {
      // Focus and position cursor at end after Turbo navigation
      setTimeout(() => {
        this.focusInputAtEnd();
      }, 50);
    }
  }

  handleTurboRender() {
    // Position cursor at end after Turbo Stream renders
    // Re-find input element in case DOM was replaced
    this.input = this.element.querySelector('.resource-search-input');
    if (this.input) {
      setTimeout(() => {
        this.positionCursorAtEnd();
      }, 50);
    }
  }

  // Focus input and position cursor at end of text
  focusInputAtEnd() {
    if (this.input) {
      this.input.focus();
      this.positionCursorAtEnd();
    }
  }

  // Position cursor at end of text (assumes input is already focused)
  positionCursorAtEnd() {
    if (this.input) {
      const length = this.input.value.length;
      this.input.setSelectionRange(length, length);
    }
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    if (this.input) {
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('keydown', this.handleKeydown);
    }
    // Remove Turbo event listeners
    if (typeof Turbo !== 'undefined' && this.boundHandleTurboLoad && this.boundHandleTurboRender) {
      document.removeEventListener('turbo:load', this.boundHandleTurboLoad);
      document.removeEventListener('turbo:render', this.boundHandleTurboRender);
    }
  }

  handleInput(event) {
    const query = event.target.value.trim();
    
    // Always clear existing timeout when user types (reset the timer)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    // If query is empty, clear results but stay on search page
    if (query.length === 0) {
      if (this.isSearchPage) {
        // Clear the search results and update URL without redirecting
        const searchUrl = this.searchUrlValue || '/resources/search';
        window.history.pushState({}, '', searchUrl);
        // Clear results by performing empty search
        this.performSearch('');
      }
      return;
    }

    // If query is less than minLength, don't search yet but hide loader if showing
    if (query.length < this.minLengthValue) {
      this.hideLoader();
      return;
    }

    // If we're on index page and query is long enough, navigate to search page via Turbo
    if (!this.isSearchPage) {
      const searchUrl = `/resources/search?q=${encodeURIComponent(query)}`;
      if (typeof Turbo !== 'undefined' && Turbo.visit) {
        Turbo.visit(searchUrl);
      } else {
        window.location.href = searchUrl;
      }
      return;
    }

    // Set a new timeout - this will fire 300ms after the last character is typed
    // If user types again before 300ms, the timeout is cleared above and reset here
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
      this.searchTimeout = null; // Clear timeout reference after execution
    }, this.debounceMsValue);
  }

  handleKeydown(event) {
    // Allow Enter key to submit form normally if user explicitly presses it
    if (event.key === 'Enter') {
      const query = event.target.value.trim();
      if (query.length >= this.minLengthValue) {
        // Clear timeout and perform immediate search (don't wait for debounce)
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
          this.searchTimeout = null;
        }
        this.performSearch(query);
      }
    }
  }

  handleSubmit(event) {
    const query = this.input.value.trim();
    
    // If query is empty, prevent submission but stay on page
    if (query.length === 0) {
      event.preventDefault();
      // Clear results but stay on search page
      if (this.isSearchPage) {
        const searchUrl = this.searchUrlValue || '/resources/search';
        window.history.pushState({}, '', searchUrl);
        this.performSearch('');
      }
      return;
    }

    // If query is less than minLength, prevent submission
    if (query.length < this.minLengthValue) {
      event.preventDefault();
      return;
    }

    // For AJAX requests, prevent default form submission
    if (event.detail && event.detail.turbo) {
      // This is a Turbo form submission, allow it
      return;
    }

    // For regular form submissions, allow default behavior
    // But we'll handle most cases via AJAX
  }

  performSearch(query) {
    const url = this.searchUrlValue || '/resources/search';

    // Push search event to GTM dataLayer for global search only (who searched for what)
    if (query && query.length > 0 && url === '/search' && typeof window.dataLayer !== 'undefined') {
      const userIdMeta = document.querySelector('meta[name="ga-user-id"]');
      const userId = userIdMeta?.getAttribute('content') || null;
      window.dataLayer.push({
        event: 'search',
        search_term: query,
        search_type: 'global',
        user_id: userId || undefined
      });
    }

    // Get current sort parameter if on search page
    const urlParams = new URLSearchParams(window.location.search);
    const sort = urlParams.get('sort');
    const queryParams = new URLSearchParams();
    if (query && query.length > 0) {
      queryParams.append('q', query);
    }
    if (sort) {
      queryParams.append('sort', sort);
    }

    // Build URL - if no query, just use base URL
    const searchUrl = query && query.length > 0 ? `${url}?${queryParams.toString()}` : url;

    // Show loader only if there's a query
    if (query && query.length > 0) {
      this.showLoader();
    } else {
      this.hideLoader();
    }

    // Update URL without reload
    window.history.pushState({}, '', searchUrl);

    // Send Turbo Stream request
    fetch(searchUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'X-Requested-With': 'XMLHttpRequest',
        'Turbo-Frame': 'search-results'
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      // Hide loader
      this.hideLoader();
      
      // Check if response is Turbo Stream
      if (html.includes('<turbo-stream')) {
        // Turbo Stream response - Turbo will handle it automatically
        if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
          Turbo.renderStreamMessage(html);
          // Re-initialize click tracker after Turbo Stream update
          setTimeout(() => {
            this.reinitializeClickTracker();
            // Position cursor at end of text after Turbo Stream update
            this.positionCursorAtEnd();
          }, 100);
        } else {
          // Fallback if Turbo is not available
          this.updateResults(html);
        }
      } else {
        // HTML response - replace the results container
        this.updateResults(html);
      }
    })
    .catch(error => {
      console.error('Search error:', error);
      // Hide loader on error
      this.hideLoader();
      // Fallback to Turbo navigation or full page reload
      if (typeof Turbo !== 'undefined' && Turbo.visit) {
        Turbo.visit(searchUrl);
      } else {
        window.location.href = searchUrl;
      }
    });
  }

  showLoader() {
    const loader = document.getElementById('search-loader');
    const container = document.querySelector('#search-results-container') || document.querySelector('.resource-search-results-container');
    if (loader && container) {
      loader.style.display = 'flex';
      // Hide existing results while loading
      const results = container.querySelector('.resource-search-results, .empty-state');
      if (results) {
        results.style.opacity = '0.5';
      }
    }
  }

  hideLoader() {
    const loader = document.getElementById('search-loader');
    const container = document.querySelector('#search-results-container') || document.querySelector('.resource-search-results-container');
    if (loader) {
      loader.style.display = 'none';
    }
    // Restore results opacity
    if (container) {
      const results = container.querySelector('.resource-search-results, .empty-state');
      if (results) {
        results.style.opacity = '1';
      }
    }
  }

  updateResults(html) {
    // Parse HTML and update results container
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const resultsContainer = doc.querySelector('#search-results-container') || doc.querySelector('.resource-search-results-container');
    
    if (resultsContainer) {
      const currentContainer = document.querySelector('#search-results-container') || document.querySelector('.resource-search-results-container');
      if (currentContainer) {
        currentContainer.innerHTML = resultsContainer.innerHTML;
        this.reinitializeClickTracker();
        // Position cursor at end of text after results update
        this.positionCursorAtEnd();
      } else {
        // If we're on index page, navigate to search page via Turbo
        const searchUrl = `/resources/search?q=${encodeURIComponent(this.input.value.trim())}`;
        if (typeof Turbo !== 'undefined' && Turbo.visit) {
          Turbo.visit(searchUrl);
        } else {
          window.location.href = searchUrl;
        }
      }
    }
  }

  reinitializeClickTracker() {
    // Re-initialize click tracker after DOM updates
    if (typeof window.safeInitializeClickTracker === 'function') {
      window.safeInitializeClickTracker();
    } else {
      // Fallback: trigger DOMContentLoaded event
      const event = new Event('DOMContentLoaded');
      document.dispatchEvent(event);
    }
  }
}

